import { ComponentProps, forwardRef } from "react";

import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { twMerge } from "tailwind-merge";

const DEFAULT_VARIANT = "outlined";

const input = cva(
  "block w-full rounded-md border-0 py-1.5 text-gray-900 placeholder:text-gray-400 sm:text-sm sm:leading-6",
  {
    variants: {
      variant: {
        outlined:
          "focus:ring-brand-400 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset",
        plain: "border-transparent focus:border-transparent focus:ring-0",
      },
    },
    defaultVariants: {
      variant: DEFAULT_VARIANT,
    },
  },
);

type NativeInputProps = ComponentProps<"input">;

type VariantInputProps = VariantProps<typeof input>;

export type Props = VariantInputProps & {
  id?: string;
  className?: string;
} & NativeInputProps;

const Input = forwardRef<HTMLInputElement, Props>(
  (
    {
      id,
      type = "text",
      variant = DEFAULT_VARIANT,
      defaultValue,
      value,
      placeholder,
      onChange,
      onBlur,
      className,
      ...rest
    },
    ref,
  ) => {
    return (
      <input
        id={id}
        type={type}
        defaultValue={defaultValue}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        onBlur={onBlur}
        className={twMerge(
          input({ variant, className }),
          rest.disabled && "opacity-50",
        )}
        ref={ref}
        {...rest}
      />
    );
  },
);

Input.displayName = "Input";

export default Input;
